import {
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import "./OutstandingFollowUps.css";
import {
  searchOutstandingCab,
  getOutstandingFollowups,
  createOutstandingFollowup,
  updateOutstandingFollowup,
  markOutstandingPaid,
  markOutstandingSmsSent,
} from "../../services/outstandingFollowup";
const getToday = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(
    date.getMonth() + 1
  ).padStart(2, "0");
  const day = String(
    date.getDate()
  ).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const formatDate = (date) => {
  if (!date) return "";
  const parts = date.split("-");
  if (parts.length !== 3) {
    return date;
  }
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};
const formatAmount = (amount) => {
  return new Intl.NumberFormat(
    "en-IN",
    {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }
  ).format(amount || 0);
};
export default function OutstandingFollowUps() {
  const [selectedDate, setSelectedDate] =
    useState(getToday());
  const [cabLastFour, setCabLastFour] =
    useState("");
  const [cabDetails, setCabDetails] =
    useState(null);
  const [outstandingAmount, setOutstandingAmount] =
    useState("");
  const [dueDate, setDueDate] =
    useState("");
  const [records, setRecords] =
    useState([]);
  const [filteredRecords, setFilteredRecords] =
    useState([]);
  const [loading, setLoading] =
    useState(false);
  const [searching, setSearching] =
    useState(false);
  const [saving, setSaving] =
    useState(false);
  const [fromDate, setFromDate] =
    useState(getToday());
  const [toDate, setToDate] =
    useState(getToday());
  const [supplierFilter, setSupplierFilter] =
    useState("");
  const [statusFilter, setStatusFilter] =
    useState("");
  const [mobilePage, setMobilePage] = useState(1);
  const [isMobileView, setIsMobileView] = useState(() => typeof window !== "undefined" && window.innerWidth <= 650);
  const [message, setMessage] =
    useState("");
  const [messageType, setMessageType] =
    useState("success");
  useEffect(() => {
    const media = window.matchMedia("(max-width: 650px)");
    const handleChange = (event) => {
      setIsMobileView(event.matches);
      setMobilePage(1);
    };
    setIsMobileView(media.matches);
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, []);
  useEffect(() => {
    setMobilePage((page) => Math.max(1, Math.min(page, Math.max(1, Math.ceil(filteredRecords.length / 3)))));
  }, [filteredRecords.length]);
  const mobileTotalPages = Math.max(1, Math.ceil(filteredRecords.length / 3));
  const visibleRecords = isMobileView ? filteredRecords.slice((mobilePage - 1) * 3, mobilePage * 3) : filteredRecords;
  const [showMessage, setShowMessage] =
    useState(false);
  const [editingId, setEditingId] =
    useState(null);
  const [showPaidModal, setShowPaidModal] =
    useState(false);
  const [paidRecord, setPaidRecord] =
    useState(null);
  const [showSmsModal, setShowSmsModal] =
    useState(false);
  const [smsRecord, setSmsRecord] =
    useState(null);
  const [smsSending, setSmsSending] =
    useState(false);
  const pageRef = useRef(null);
  const showToast = useCallback(
    (type, text) => {
      setMessageType(type);
      setMessage(text);
      setShowMessage(true);
      setTimeout(() => {
        setShowMessage(false);
      }, 3500);
    },
    []
  );
  const searchCab = async () => {
    const value = cabLastFour.trim();
    if (!/^\d{4}$/.test(value)) {
      showToast(
        "error",
        "Please enter exactly 4 numerical digits."
      );
      return;
    }
    if (!selectedDate) {
      showToast(
        "error",
        "Please select the follow-up date."
      );
      return;
    }
    try {
      setSearching(true);
      const cab =
        await searchOutstandingCab({
          cabLast4: value,
          followupDate: selectedDate,
        });
      if (!cab) {
        setCabDetails(null);
        showToast(
          "error",
          "No active vehicle with outstanding amount found."
        );
        return;
      }
      setCabDetails(cab);
      setOutstandingAmount(
        cab.outstandingAmount !== undefined &&
        cab.outstandingAmount !== null
          ? String(
              cab.outstandingAmount
            )
          : ""
      );
      setDueDate(
        cab.dueDate || ""
      );
      setEditingId(null);
      setShowMessage(false);
    } catch (error) {
      setCabDetails(null);
      showToast(
        "error",
        error.message
      );
    } finally {
      setSearching(false);
    }
  };
  const loadRecords = useCallback(
    async () => {
      try {
        setLoading(true);
        const data =
          await getOutstandingFollowups({
            fromDate,
            toDate,
            supplierId: supplierFilter,
            status: statusFilter,
          });
        setRecords(data);
        setFilteredRecords(data);
      } catch (error) {
        showToast(
          "error",
          error.message
        );
      } finally {
        setLoading(false);
      }
    },
    [
      fromDate,
      toDate,
      supplierFilter,
      statusFilter,
      showToast,
    ]
  );
  useEffect(() => {
    loadRecords();
  }, [loadRecords]);
  const updateFollowUp = async () => {
    if (!selectedDate) {
      showToast(
        "error",
        "Please select the follow-up date."
      );
      return;
    }
    if (!cabDetails) {
      showToast(
        "error",
        "Please search the cab first."
      );
      return;
    }
    const amount = Number(
      String(
        outstandingAmount
      ).replace(
        /[^0-9.]/g,
        ""
      )
    );
    if (!amount || amount <= 0) {
      showToast(
        "error",
        "Outstanding amount was not fetched."
      );
      return;
    }
    if (!dueDate) {
      showToast(
        "error",
        "Please select the due date."
      );
      return;
    }
    try {
      setSaving(true);
      const payload = {
        outstandingSupplierId:
          cabDetails.outstandingSupplierId,
        supplierId:
          cabDetails.supplierId,
        vehicleAssignmentId:
          cabDetails.vehicleAssignmentId,
        vehicleId:
          cabDetails.vehicleId,
        followupDate:
          selectedDate,
        outstandingAmount:
          amount,
        dueDate:
          dueDate,
      };
      if (editingId) {
        await updateOutstandingFollowup(
          editingId,
          payload
        );
        showToast(
          "success",
          "Outstanding follow-up updated successfully."
        );
      } else {
        await createOutstandingFollowup(
          payload
        );
        showToast(
          "success",
          "Outstanding follow-up added successfully."
        );
      }
      await loadRecords();
      clearForm();
    } catch (error) {
      showToast(
        "error",
        error.message
      );
    } finally {
      setSaving(false);
    }
  };
  const editRecord = (record) => {
    setSelectedDate(
      record.date || getToday()
    );
    setCabLastFour(
      String(
        record.cabNumber || ""
      ).slice(-4)
    );
    setCabDetails({
      outstandingSupplierId:
        record.outstandingSupplierId,
      supplierId:
        record.supplierId,
      vehicleAssignmentId:
        record.vehicleAssignmentId,
      vehicleId:
        record.vehicleId,
      cabNumber:
        record.cabNumber,
      supplier:
        record.supplier,
      driver:
        record.driver,
      contacts:
        record.contacts || [],
      vehicleType:
        record.vehicleType,
      year:
        record.year,
    });
    setOutstandingAmount(
      record.outstandingAmount !== null &&
      record.outstandingAmount !== undefined
        ? String(
            record.outstandingAmount
          )
        : ""
    );
    setDueDate(
      record.dueDate || ""
    );
    setEditingId(
      record.id
    );
    setShowMessage(false);
    if (pageRef.current) {
      pageRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };
  const openSmsModal = (record) => {
    setSmsRecord(record);
    setShowSmsModal(true);
  };
  const closeSmsModal = () => {
    setShowSmsModal(false);
    setSmsRecord(null);
    setSmsSending(false);
  };
  const confirmSendSms = async () => {
    if (!smsRecord) {
      return;
    }
    try {
      setSmsSending(true);
      await markOutstandingSmsSent(
        smsRecord.id
      );
      closeSmsModal();
      await loadRecords();
      showToast(
        "success",
        "Outstanding SMS sent successfully."
      );
    } catch (error) {
      showToast(
        "error",
        error.message
      );
    } finally {
      setSmsSending(false);
    }
  };
  const openPaidModal = (record) => {
    setPaidRecord(record);
    setShowPaidModal(true);
  };
  const confirmPaid = async () => {
    if (!paidRecord) {
      return;
    }
    try {
      await markOutstandingPaid(
        paidRecord.id
      );
      setShowPaidModal(false);
      setPaidRecord(null);
      await loadRecords();
      showToast(
        "success",
        "Payment marked as paid successfully."
      );
    } catch (error) {
      showToast(
        "error",
        error.message
      );
    }
  };
  const clearForm = () => {
    setCabLastFour("");
    setCabDetails(null);
    setOutstandingAmount("");
    setDueDate("");
    setEditingId(null);
  };
  const filterRecords = async () => {
    if (!fromDate || !toDate) {
      showToast(
        "error",
        "Please select both From Date and To Date."
      );
      return;
    }
    await loadRecords();
  };
  const refreshRecords = async () => {
    setFromDate(getToday());
    setToDate(getToday());
    setSupplierFilter("");
    setStatusFilter("");
    try {
      setLoading(true);
      const data =
        await getOutstandingFollowups();
      setRecords(data);
      setFilteredRecords(data);
    } catch (error) {
      showToast(
        "error",
        error.message
      );
    } finally {
      setLoading(false);
    }
  };
  const downloadReport = () => {
    if (!filteredRecords.length) {
      showToast(
        "error",
        "No records available for the selected date range."
      );
      return;
    }
    const headers = [
      "S.No",
      "Date",
      "Cab Number",
      "Supplier",
      "Driver",
      "Contact Numbers",
      "Vehicle Type",
      "Year",
      "Outstanding Amount",
      "Due Date",
      "SMS",
      "Status",
      "Remarks",
      "Updated By",
      "Updated At",
    ];
    const rows =
      filteredRecords.map(
        (record, index) => [
          index + 1,
          record.date,
          record.cabNumber,
          record.supplier,
          record.driver,
          record.contacts?.join(" / ") || "",
          record.vehicleType,
          record.year,
          record.outstandingAmount,
          record.dueDate,
          record.smsSent
            ? "Sent"
            : "Not Sent",
          record.status,
          record.remarks,
          record.updatedByName || "",
          record.updatedAt || "",
        ]
      );
    const csv = [
      headers,
      ...rows,
    ]
      .map((row) =>
        row
          .map(
            (value) =>
              `"${String(
                value ?? ""
              ).replace(
                /"/g,
                '""'
              )}"`
          )
          .join(",")
      )
      .join("\n");
    const blob = new Blob(
      [csv],
      {
        type:
          "text/csv;charset=utf-8;",
      }
    );
    const url =
      URL.createObjectURL(
        blob
      );
    const link =
      document.createElement(
        "a"
      );
    link.href = url;
    link.download =
      `Outstanding_Followup_Report\_${fromDate}\_to\_${toDate}.csv`;
    document.body.appendChild(
      link
    );
    link.click();
    document.body.removeChild(
      link
    );
    URL.revokeObjectURL(url);
    showToast(
      "success",
      "Outstanding follow-up report downloaded successfully."
    );
  };
  const suppliers = [
    ...new Set(
      records
        .map(
          (record) =>
            record.supplier
        )
        .filter(Boolean)
    ),
  ];
  return (
    <div className="outstanding-layout">
      <div className="outstanding-content">
        <div
          className="outstanding-page-container"
          ref={pageRef}
        >
          <div className="outstanding-page">
            {showMessage && (
              <div
                className={`outstanding-message ${
                  messageType === "error"
                    ? "outstanding-message-error"
                    : "outstanding-message-success"
                }`}
              >
                <span>
                  {messageType === "error"
                    ? "!"
                    : "✓"}
                </span>
                <p>
                  {message}
                </p>
                <button
                  type="button"
                  onClick={() =>
                    setShowMessage(
                      false
                    )
                  }
                >
                  ×
                </button>
              </div>
            )}
            <section className="outstanding-card">
              <div className="outstanding-card-title">
                Add Outstanding Follow-up
              </div>
              <div className="outstanding-search-form">
                <div className="outstanding-form-group">
                  <label>
                    Date{" "}
                    <span>\*</span>
                  </label>
                  <input
                    type="date"
                    value={
                      selectedDate
                    }
                    onChange={(e) =>
                      setSelectedDate(
                        e.target.value
                      )
                    }
                  />
                </div>
                <div className="outstanding-form-group">
                  <label>
                    Cab Last 4 Digits{" "}
                    <span>\*</span>
                  </label>
                  <input
                    type="text"
                    value={
                      cabLastFour
                    }
                    maxLength={4}
                    inputMode="numeric"
                    placeholder="Enter last 4 digits"
                    onChange={(e) =>
                      setCabLastFour(
                        e.target.value.replace(
                          /\D/g,
                          ""
                        )
                      )
                    }
                    onKeyDown={(e) => {
                      if (
                        e.key ===
                        "Enter"
                      ) {
                        searchCab();
                      }
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="outstanding-primary-btn"
                  onClick={
                    searchCab
                  }
                  disabled={
                    searching
                  }
                >
                  {searching
                    ? "Searching..."
                    : "Search Cab"}
                </button>
              </div>
            </section>
            {cabDetails && (
              <section className="outstanding-card">
                <div className="outstanding-card-header">
                  <div className="outstanding-card-title">
                    Outstanding Details
                  </div>
                  <div className="outstanding-card-header-actions">
                    <button
                      type="button"
                      className="outstanding-secondary-btn"
                      onClick={
                        clearForm
                      }
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      className="outstanding-primary-btn"
                      onClick={
                        updateFollowUp
                      }
                      disabled={
                        saving
                      }
                    >
                      {saving
                        ? "Saving..."
                        : editingId
                        ? "Update"
                        : "Add"}
                    </button>
                  </div>
                </div>
                <div className="outstanding-details">
                  <div className="outstanding-detail-item">
                    <span>
                      Cab Number
                    </span>
                    <strong className="outstanding-cab-number">
                      {
                        cabDetails.cabNumber
                      }
                    </strong>
                  </div>
                  <div className="outstanding-detail-item">
                    <span>
                      Supplier / Driver
                    </span>
                    <strong>
                      {
                        cabDetails.supplier
                      }
                    </strong>
                    <small>
                      {
                        cabDetails.driver
                      }
                    </small>
                  </div>
                  <div className="outstanding-detail-item">
                    <span>
                      Contact Number
                    </span>
                    <div className="outstanding-contact-list">
                      {cabDetails.contacts?.map(
                        (contact) => (
                          <a
                            href={`tel:${contact}`}
                            key={
                              contact
                            }
                          >
                            {contact}
                          </a>
                        )
                      )}
                    </div>
                  </div>
                  <div className="outstanding-detail-item">
                    <span>
                      Vehicle Type
                    </span>
                    <strong>
                      {
                        cabDetails.vehicleType
                      }
                    </strong>
                  </div>
                  <div className="outstanding-detail-item">
                    <span>
                      Year
                    </span>
                    <strong>
                      {
                        cabDetails.year
                      }
                    </strong>
                  </div>
                </div>
                <div className="outstanding-payment-row">
                  <div className="outstanding-form-group">
                    <label>
                      Outstanding Amount{" "}
                      <span>\*</span>
                    </label>
                    <input
                      type="text"
                      value={
                        outstandingAmount
                          ? formatAmount(
                              Number(
                                outstandingAmount
                              )
                            )
                          : ""
                      }
                      placeholder="Amount fetched automatically"
                      readOnly
                      className="outstanding-readonly-amount"
                    />
                  </div>
                  <div className="outstanding-form-group">
                    <label>
                      Due Date{" "}
                      <span>\*</span>
                    </label>
                    <input
                      type="date"
                      value={
                        dueDate
                      }
                      onChange={(e) =>
                        setDueDate(
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div className="outstanding-status-preview">
                    <span>
                      Status
                    </span>
                    <strong>
                      {dueDate &&
                      new Date(
                        dueDate
                      ) <
                        new Date(
                          `${selectedDate}T00:00:00`
                        )
                        ? "Overdue"
                        : "Pending"}
                    </strong>
                  </div>
                </div>
              </section>
            )}
            <section className="outstanding-card outstanding-list-card">
              <div className="outstanding-list-header">
                <div className="outstanding-card-title">
                  Outstanding Follow-ups List
                </div>
              </div>
              <div className="outstanding-filter-row">
  <div className="outstanding-filter-group">
    <label>From Date</label>
    <input
      type="date"
      value={fromDate}
      onChange={(e) =>
        setFromDate(e.target.value)
      }
    />
  </div>
  <div className="outstanding-filter-group">
    <label>To Date</label>
    <input
      type="date"
      value={toDate}
      onChange={(e) =>
        setToDate(e.target.value)
      }
    />
  </div>
  <div className="outstanding-filter-group outstanding-supplier-filter">
    <label>Supplier</label>
    <select
      value={supplierFilter}
      onChange={(e) =>
        setSupplierFilter(e.target.value)
      }
    >
      <option value="">
        All Suppliers
      </option>
      {suppliers.map((supplier) => (
        <option
          key={supplier}
          value={supplier}
        >
          {supplier}
        </option>
      ))}
    </select>
  </div>
  <div className="outstanding-filter-group outstanding-status-filter">
    <label>Status</label>
    <select
      value={statusFilter}
      onChange={(e) =>
        setStatusFilter(e.target.value)
      }
    >
      <option value="">
        All Status
      </option>
      <option value="Pending">
        Pending
      </option>
      <option value="Overdue">
        Overdue
      </option>
      <option value="Paid">
        Paid
      </option>
    </select>
  </div>
</div>
              <div className="outstanding-mobile-list">{loading ? <div className="outstanding-mobile-empty">Loading outstanding follow-ups...</div> : visibleRecords.length === 0 ? <div className="outstanding-mobile-empty">No outstanding follow-up records found for the selected filters.</div> : visibleRecords.map((record) => (<article className="outstanding-mobile-card" key={`mobile-${record.id}`}><div className="outstanding-mobile-grid"><div className="outstanding-mobile-field"><span>DATE</span><strong>{record.date || "—"}</strong></div><div className="outstanding-mobile-field"><span>CAB NUMBER</span><strong className="outstanding-mobile-cab">{record.cabNumber || "—"}</strong></div><div className="outstanding-mobile-field"><span>VEHICLE TYPE</span><strong>{record.vehicleType || "—"}</strong></div><div className="outstanding-mobile-field"><span>SUPPLIER / DRIVER</span><strong>{record.supplier || "—"}</strong><small>{record.driver || "—"}</small></div><div className="outstanding-mobile-field"><span>CONTACT NUMBER</span><div className="outstanding-mobile-contacts">{record.contacts?.length ? record.contacts.map((contact) => (<a href={`tel:${contact}`} key={contact}>{contact}</a>)) : <strong>—</strong>}</div></div><div className="outstanding-mobile-field"><span>YEAR</span><strong>{record.year || "—"}</strong></div><div className="outstanding-mobile-field outstanding-mobile-amount"><span>OUTSTANDING</span><strong>{formatAmount(record.outstandingAmount)}</strong></div><div className="outstanding-mobile-field"><span>DUE DATE</span><strong>{record.dueDate || "—"}</strong></div><div className="outstanding-mobile-field"><span>SMS</span>{record.smsSent ? <span className="outstanding-sms-sent-badge">Sent</span> : <button type="button" className="outstanding-sms-list-btn" onClick={() => openSmsModal(record)}>Send SMS</button>}</div><div className="outstanding-mobile-field"><span>STATUS</span><span className={`outstanding-status ${record.status === "Overdue" ? "outstanding-status-overdue" : record.status === "Paid" ? "outstanding-status-paid" : "outstanding-status-pending"}`}>{record.status || "Pending"}</span></div><div className="outstanding-mobile-field"><span>REMARKS</span><strong className="outstanding-mobile-truncate">{record.remarks || "—"}</strong></div><div className="outstanding-mobile-field"><span>UPDATED BY</span><strong className="outstanding-mobile-updated-value">{record.updatedByName || "—"}</strong></div><div className="outstanding-mobile-field"><span>UPDATED AT</span><strong className="outstanding-mobile-updated-value">{record.updatedAt || "—"}</strong></div><div className="outstanding-mobile-field outstanding-mobile-actions-field"><span>ACTIONS</span><div className="outstanding-actions"><button type="button" className="outstanding-edit-btn" title="Edit" onClick={() => editRecord(record)}>✎</button>{record.status !== "Paid" && <button type="button" className="outstanding-paid-btn" title="Mark Paid" onClick={() => openPaidModal(record)}>✓</button>}</div></div></div></article>))}</div>
              <div className="outstanding-table-wrapper">
                <table className="outstanding-table"><colgroup><col className="col-sno" /><col className="col-date" /><col className="col-cab" /><col className="col-supplier" /><col className="col-contact" /><col className="col-vehicle" /><col className="col-year" /><col className="col-amount" /><col className="col-due" /><col className="col-sms" /><col className="col-status" /><col className="col-remarks" /><col className="col-updated-by" /><col className="col-updated-at" /><col className="col-actions" /></colgroup>
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Date</th>
                      <th>Cab Number</th>
                      <th>
                        Supplier / Driver
                      </th>
                      <th>
                        Contact Number
                      </th>
                      <th>
                        Vehicle Type
                      </th>
                      <th>Year</th>
                      <th>
                        Outstanding
                      </th>
                      <th>
                        Due Date
                      </th>
                      <th>
                        SMS
                      </th>
                      <th>
                        Status
                      </th>
                      <th>
                        Remarks
                      </th>
                      <th>
                        Updated By
                      </th>
                      <th>
                        Updated At
                      </th>
                      <th>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan="15"
                          className="outstanding-empty"
                        >
                          Loading outstanding follow-ups...
                        </td>
                      </tr>
                    ) : filteredRecords.length > 0 ? (
                      visibleRecords.map(
                        (
                          record,
                          index
                        ) => (
                          <tr
                            key={
                              record.id
                            }
                          >
                            <td>
                              {index + 1}
                            </td>
                            <td>
                              {
                                record.date
                              }
                            </td>
                            <td>
                              <strong className="outstanding-table-cab">
                                {
                                  record.cabNumber
                                }
                              </strong>
                            </td>
                            <td>
                              <div className="outstanding-table-person">
                                <strong>
                                  {
                                    record.supplier
                                  }
                                </strong>
                                <span>
                                  {
                                    record.driver
                                  }
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="outstanding-table-contacts">
                                {record.contacts?.map(
                                  (
                                    contact
                                  ) => (
                                    <a
                                      href={`tel:${contact}`}
                                      key={
                                        contact
                                      }
                                    >
                                      {
                                        contact
                                      }
                                    </a>
                                  )
                                )}
                              </div>
                            </td>
                            <td>
                              {
                                record.vehicleType
                              }
                            </td>
                            <td>
                              {
                                record.year
                              }
                            </td>
                            <td>
                              <strong className="outstanding-table-amount">
                                {formatAmount(
                                  record.outstandingAmount
                                )}
                              </strong>
                            </td>
                            <td>
                              {
                                record.dueDate
                              }
                            </td>
                            <td>
                              {record.smsSent ? (
                                <span className="outstanding-sms-sent-badge">
                                  Sent
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  className="outstanding-sms-list-btn"
                                  onClick={() =>
                                    openSmsModal(
                                      record
                                    )
                                  }
                                >
                                  Send SMS
                                </button>
                              )}
                            </td>
                            <td>
                              <span
                                className={`outstanding-status ${
                                  record.status ===
                                  "Overdue"
                                    ? "outstanding-status-overdue"
                                    : record.status ===
                                      "Paid"
                                    ? "outstanding-status-paid"
                                    : "outstanding-status-pending"
                                }`}
                              >
                                {
                                  record.status
                                }
                              </span>
                            </td>
                            <td>
                              <span className="outstanding-table-remarks">
                                {
                                  record.remarks
                                }
                              </span>
                            </td>
                            <td>
                              {
                                record.updatedByName ||
                                "—"
                              }
                            </td>
                            <td>
                              {
                                record.updatedAt
                              }
                            </td>
                            <td>
                              <div className="outstanding-actions">
                                <button
                                  type="button"
                                  className="outstanding-edit-btn"
                                  title="Edit"
                                  onClick={() =>
                                    editRecord(
                                      record
                                    )
                                  }
                                >
                                  ✎
                                </button>
                                {record.status !==
                                  "Paid" && (
                                  <button
                                    type="button"
                                    className="outstanding-paid-btn"
                                    title="Mark Paid"
                                    onClick={() =>
                                      openPaidModal(
                                        record
                                      )
                                    }
                                  >
                                    ✓
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan="15"
                          className="outstanding-empty"
                        >
                          No outstanding follow-up records found for the selected filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="outstanding-table-footer">
                <span>
                  Showing {isMobileView ? visibleRecords.length : filteredRecords.length} of {filteredRecords.length} entries
                </span>
                <div className="outstanding-pagination">
                  <button
                    type="button"
                    disabled={!isMobileView || mobilePage <= 1}
                    onClick={() => setMobilePage((page) => Math.max(1, page - 1))}
                  >
                    ‹
                  </button>
                  {isMobileView ? (
                    Array.from({ length: mobileTotalPages }, (_, pageIndex) => (
                      <button
                        key={pageIndex + 1}
                        type="button"
                        className={mobilePage === pageIndex + 1 ? "active" : ""}
                        onClick={() => setMobilePage(pageIndex + 1)}
                      >
                        {pageIndex + 1}
                      </button>
                    ))
                  ) : (
                    <button type="button" className="active">1</button>
                  )}
                  <button
                    type="button"
                    disabled={!isMobileView || mobilePage >= mobileTotalPages}
                    onClick={() => setMobilePage((page) => Math.min(mobileTotalPages, page + 1))}
                  >
                    ›
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      {showPaidModal && (
        <div className="outstanding-modal-overlay">
          <div className="outstanding-confirm-modal">
            <button
              type="button"
              className="outstanding-modal-close"
              onClick={() => {
                setShowPaidModal(
                  false
                );
                setPaidRecord(
                  null
                );
              }}
            >
              ×
            </button>
            <div className="outstanding-modal-icon outstanding-modal-icon-paid">
              ✓
            </div>
            <h2>
              Confirm Payment
            </h2>
            <p>
              Are you sure you want to
              mark this outstanding amount
              as paid?
            </p>
            {paidRecord && (
              <div className="outstanding-modal-details">
                <div>
                  <span>
                    Supplier
                  </span>
                  <strong>
                    {
                      paidRecord.supplier
                    }
                  </strong>
                </div>
                <div>
                  <span>
                    Vehicle
                  </span>
                  <strong>
                    {
                      paidRecord.cabNumber
                    }
                  </strong>
                </div>
                <div>
                  <span>
                    Amount
                  </span>
                  <strong>
                    {formatAmount(
                      paidRecord.outstandingAmount
                    )}
                  </strong>
                </div>
              </div>
            )}
            <div className="outstanding-modal-actions">
              <button
                type="button"
                className="outstanding-modal-cancel"
                onClick={() => {
                  setShowPaidModal(
                    false
                  );
                  setPaidRecord(
                    null
                  );
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="outstanding-modal-confirm"
                onClick={
                  confirmPaid
                }
              >
                Confirm Paid
              </button>
            </div>
          </div>
        </div>
      )}
      {showSmsModal && (
        <div className="outstanding-modal-overlay">
          <div className="outstanding-confirm-modal outstanding-sms-modal">
            <button
              type="button"
              className="outstanding-modal-close"
              onClick={
                closeSmsModal
              }
            >
              ×
            </button>
            <h2>
              Send Outstanding SMS
            </h2>
            {smsRecord && (
              <>
                <div className="outstanding-modal-details">
                  <div>
                    <span>
                      Recipient
                    </span>
                    <strong>
                      {smsRecord.driver ||
                        smsRecord.supplier ||
                        "—"}
                    </strong>
                  </div>
                  <div>
                    <span>
                      Vehicle
                    </span>
                    <strong>
                      {
                        smsRecord.cabNumber
                      }
                    </strong>
                  </div>
                  <div>
                    <span>
                      Outstanding
                    </span>
                    <strong>
                      {formatAmount(
                        smsRecord.outstandingAmount
                      )}
                    </strong>
                  </div>
                  <div>
                    <span>
                      Due Date
                    </span>
                    <strong>
                      {formatDate(
                        smsRecord.dueDate
                      )}
                    </strong>
                  </div>
                </div>
                <div className="outstanding-sms-preview">
                  <div className="outstanding-sms-preview-title">
                    SMS Preview
                  </div>
                  <p>
                    Dear{" "}
                    <strong>
                      {smsRecord.driver ||
                        smsRecord.supplier ||
                        "Driver"}
                    </strong>
                    , Reminder to clear
                    the outstanding amount of{" "}
                    <strong>
                      Rs.{" "}
                      {Number(
                        smsRecord.outstandingAmount ||
                        0
                      ).toLocaleString(
                        "en-IN"
                      )}
                      /-
                    </strong>{" "}
                    for cab No.{" "}
                    <strong>
                      {
                        smsRecord.cabNumber
                      }
                    </strong>{" "}
                    by{" "}
                    <strong>
                      {formatDate(
                        smsRecord.dueDate
                      )}
                    </strong>
                    .
                  </p>
                  <div className="outstanding-sms-signature">
                    Srinivasa
                  </div>
                </div>
              </>
            )}
            <div className="outstanding-modal-actions">
              <button
                type="button"
                className="outstanding-modal-cancel"
                onClick={
                  closeSmsModal
                }
                disabled={
                  smsSending
                }
              >
                Cancel
              </button>
              <button
                type="button"
                className="outstanding-modal-confirm"
                onClick={
                  confirmSendSms
                }
                disabled={
                  smsSending
                }
              >
                {smsSending
                  ? "Sending..."
                  : "Send SMS"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}